import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { downloadCsv } from "../lib/csv";
import { useOrganization } from "../organization/OrganizationProvider";
import "./ImportDetailPage.css";

type ImportItem = {
  id: string;
  purchase_order_item_id: string;
  catalog_item_id: string | null;
  sku: string | null;
  description: string;
  quantity: number | string;
  unit_cost: number | string;
  currency_code: string;
  unit_weight_kg: number | string | null;
  unit_volume_m3: number | string | null;
};

type ImportDetail = {
  id: string;
  organization_id: string;
  purchase_order_id: string;
  purchase_number: string;
  supplier_id: string;
  supplier_name: string;
  import_number: string;
  status: string;
  origin_country_code: string | null;
  destination_country_code: string | null;
  incoterm: string | null;
  supplier_currency_code: string | null;
  exchange_rate: number | string | null;
  exchange_rate_date: string | null;
  exchange_rate_source: string | null;
  shipment_date: string | null;
  estimated_arrival_date: string | null;
  arrival_date: string | null;
  customs_clearance_date: string | null;
  customs_office: string | null;
  customs_declaration_number: string | null;
  transport_reference: string | null;
  allocation_method: string | null;
  notes: string | null;
  updated_at: string;
  items: ImportItem[];
};

type LandedCostSummary = {
  merchandise_cost_pen: number | string;
  capitalizable_cost_pen: number | string;
  landed_cost_pen: number | string;
  recoverable_tax_pen: number | string;
  noncapitalizable_cost_pen: number | string;
  item_count: number | string;
};

type ImportItemLandedCost = {
  catalog_item_id: string;
  sku: string | null;
  item_name: string;
  quantity: number | string;
  purchase_cost_pen: number | string;
  allocated_cost_pen: number | string;
  landed_cost_pen: number | string;
  landed_unit_cost_pen: number | string;
  current_cost_price: number | string;
  current_sale_price: number | string;
};

type ImportDocument = {
  id: string;
  document_type: string;
  document_scope: string;
  supplier_id: string | null;
  supplier_name: string;
  series: string | null;
  document_number: string;
  issue_date: string;
  currency_code: string;
  exchange_rate: number | string;
  is_national: boolean;
  taxable_amount: number | string;
  tax_amount: number | string;
  total_amount: number | string;
  fob_amount: number | string;
  item_count: number | string;
  document_reference: string | null;
  notes: string | null;
};

type ImportAnalysis = {
  document_count: number | string;
  domestic_document_count: number | string;
  foreign_document_count: number | string;
  document_total_pen: number | string;
  domestic_igv_pen: number | string;
  foreign_fob: number | string;
  customs_fob: number | string;
  customs_freight: number | string;
  customs_insurance: number | string;
  customs_value: number | string;
  customs_total_payable_pen: number | string;
  merchandise_cost_pen: number | string;
  capitalizable_cost_pen: number | string;
  landed_cost_pen: number | string;
  recoverable_tax_pen: number | string;
  cif_fob_factor: number | string;
  landed_factor: number | string;
  tax_burden_percent: number | string;
};

type ClosureDiagnostic = {
  import_shipment_id: string;
  import_number: string;
  purchase_order_id: string;
  purchase_number: string;
  diagnostic_status: string;
  ready_to_close: boolean;
  purchase_status: string;
  shipment_status: string;
  ordered_quantity: number | string;
  received_quantity: number | string;
  shipment_quantity: number | string;
  receipt_quantity: number | string;
  inventory_quantity: number | string;
  merchandise_pen: number | string;
  allocated_cost_pen: number | string;
  landed_cost_pen: number | string;
  receipt_value_pen: number | string;
  inventory_value_pen: number | string;
  declaration_count: number | string;
  liquidated_declaration_count: number | string;
  purchase_received_ok: boolean;
  shipment_received_ok: boolean;
  purchase_quantity_ok: boolean;
  shipment_quantity_ok: boolean;
  receipt_quantity_ok: boolean;
  inventory_quantity_ok: boolean;
  landed_cost_exists_ok: boolean;
  customs_declaration_ok: boolean;
  receipt_landed_reconciliation_ok: boolean;
  inventory_landed_reconciliation_ok: boolean;
  receipt_inventory_reconciliation_ok: boolean;
  blocking_reason: string | null;
};

const IMPORT_DOCUMENT_LABELS: Record<string, string> = {
  invoice: "Factura / Invoice",
  quotation: "Cotización",
  sales_note: "Nota de venta",
  credit_note: "Nota de crédito",
  debit_note: "Nota de débito",
  freight: "Flete",
  insurance: "Seguro",
  customs_agent: "Agente de aduanas",
  transport: "Transporte",
  packing_list: "Packing list",
  receipt: "Comprobante",
  other: "Otro",
};

const IMPORT_STATUS_LABELS: Record<string, string> = {
  draft: "Borrador",
  open: "Abierta",
  in_transit: "En tránsito",
  arrived: "Arribada",
  customs: "En aduanas",
  cleared: "Nacionalizada",
  received: "Recibida",
  closed: "Cerrada",
  cancelled: "Cancelada",
};

function money(value: number | string | null) {
  return new Intl.NumberFormat("es-PE", {
    style: "currency",
    currency: "PEN",
  }).format(Number(value || 0));
}

function sourceMoney(value: number | string | null, currency = "USD") {
  return new Intl.NumberFormat("es-PE", {
    style: "currency",
    currency: currency || "USD",
  }).format(Number(value || 0));
}

function checkLabel(value: boolean) {
  return value ? "OK" : "Pendiente";
}

export function ImportDetailPage() {
  const { importId } = useParams();
  const { selectedOrganization } = useOrganization();

  const [detail, setDetail] = useState<ImportDetail | null>(null);
  const [landedCost, setLandedCost] = useState<LandedCostSummary | null>(null);
  const [diagnostic, setDiagnostic] = useState<ClosureDiagnostic | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [costBusy, setCostBusy] = useState(false);
  const [costType, setCostType] = useState("freight");
  const [costDescription, setCostDescription] = useState("");
  const [costCurrency, setCostCurrency] = useState("USD");
  const [costAmount, setCostAmount] = useState("");
  const [costExchangeRate, setCostExchangeRate] = useState("1");
  const [costCapitalizable, setCostCapitalizable] = useState(true);
  const [costRecoverableTax, setCostRecoverableTax] = useState(false);
  const [costAllocation, setCostAllocation] = useState("value");
  const [costSuccess, setCostSuccess] = useState<string | null>(null);
  const [itemCosts, setItemCosts] = useState<ImportItemLandedCost[]>([]);
  const [markupPercent, setMarkupPercent] = useState("30");
  const [pricingBusy, setPricingBusy] = useState(false);
  const [documents, setDocuments] = useState<ImportDocument[]>([]);
  const [analysis, setAnalysis] = useState<ImportAnalysis | null>(null);

  const [documentBusy, setDocumentBusy] = useState(false);
  const [documentType, setDocumentType] = useState("invoice");
  const [documentScope, setDocumentScope] = useState("foreign");
  const [documentSeries, setDocumentSeries] = useState("");
  const [documentNumber, setDocumentNumber] = useState("");
  const [documentDate, setDocumentDate] = useState(new Date().toISOString().slice(0, 10));
  const [documentCurrency, setDocumentCurrency] = useState("USD");
  const [documentExchangeRate, setDocumentExchangeRate] = useState("1");
  const [documentNational, setDocumentNational] = useState(false);
  const [documentTaxRate, setDocumentTaxRate] = useState("18");
  const [documentTotal, setDocumentTotal] = useState("");
  const [documentFob, setDocumentFob] = useState("");
  const [documentReference, setDocumentReference] = useState("");
  const [documentNotes, setDocumentNotes] = useState("");
  const [documentIncludeItems, setDocumentIncludeItems] = useState(true);

  const [customsBusy, setCustomsBusy] = useState(false);
  const [duaType, setDuaType] = useState("DAM");
  const [duaNumber, setDuaNumber] = useState("");
  const [duaOffice, setDuaOffice] = useState("");
  const [duaDate, setDuaDate] = useState(new Date().toISOString().slice(0, 10));
  const [duaTc, setDuaTc] = useState("1");
  const [duaFob, setDuaFob] = useState("");
  const [duaFreight, setDuaFreight] = useState("");
  const [duaInsurance, setDuaInsurance] = useState("");
  const [duaAdjustments, setDuaAdjustments] = useState("0");
  const [duaCustomsValue, setDuaCustomsValue] = useState("");
  const [duaAdValoremRate, setDuaAdValoremRate] = useState("0");
  const [duaAdValoremManual, setDuaAdValoremManual] = useState("");
  const [duaIscRate, setDuaIscRate] = useState("0");
  const [duaIscManual, setDuaIscManual] = useState("");
  const [duaIgvRate, setDuaIgvRate] = useState("18");
  const [duaIgvManual, setDuaIgvManual] = useState("");
  const [duaPerceptionRate, setDuaPerceptionRate] = useState("0");
  const [duaPerceptionManual, setDuaPerceptionManual] = useState("");

  const load = useCallback(async () => {
    if (!importId || !selectedOrganization) return;

    setLoading(true);
    setError(null);

    const [
      detailResult,
      costResult,
      diagnosticResult,
      itemCostResult,
      documentsResult,
      analysisResult,
    ] = await Promise.all([
      supabase.rpc("get_import_shipment_detail", {
        p_import_shipment_id: importId,
      }),
      supabase.rpc("get_import_landed_cost_summary", {
        p_import_shipment_id: importId,
      }),
      supabase.rpc("get_import_closure_diagnostic", {
        p_import_shipment_id: importId,
      }),
      supabase.rpc("list_import_item_landed_costs", {
        p_import_shipment_id: importId,
      }),
      supabase.rpc("list_import_documents", {
        p_import_shipment_id: importId,
      }),
      supabase.rpc("get_import_analysis", {
        p_import_shipment_id: importId,
      }),
    ]);

    setLoading(false);

    if (detailResult.error) {
      setDetail(null);
      setError(detailResult.error.message);
      return;
    }

    const nextDetail = detailResult.data as ImportDetail;

    if (
      nextDetail.organization_id !== selectedOrganization.organizationId
    ) {
      setDetail(null);
      setError("La importación pertenece a otra empresa.");
      return;
    }

    setDetail(nextDetail);

    if (costResult.error) {
      setLandedCost(null);
      setError((current) =>
        current
          ? `${current} · ${costResult.error?.message ?? ""}`
          : costResult.error?.message ?? "No se pudo cargar el costo landed.",
      );
    } else {
      setLandedCost(
        ((costResult.data ?? [])[0] ?? null) as LandedCostSummary | null,
      );
    }

    if (diagnosticResult.error) {
      setDiagnostic(null);
      setError((current) =>
        current
          ? `${current} · ${diagnosticResult.error?.message ?? ""}`
          : diagnosticResult.error?.message ??
            "No se pudo cargar el diagnóstico de cierre.",
      );
    } else {
      setDiagnostic(
        ((diagnosticResult.data ?? [])[0] ?? null) as ClosureDiagnostic | null,
      );
    }

    setItemCosts(
      itemCostResult.error
        ? []
        : ((itemCostResult.data ?? []) as ImportItemLandedCost[]),
    );

    setDocuments(
      documentsResult.error
        ? []
        : ((documentsResult.data ?? []) as ImportDocument[]),
    );

    setAnalysis(
      analysisResult.error
        ? null
        : (((analysisResult.data ?? [])[0] ?? null) as ImportAnalysis | null),
    );
  }, [importId, selectedOrganization]);

  useEffect(() => {
    setDetail(null);
    setLandedCost(null);
    setDiagnostic(null);
    setError(null);
    void load();
  }, [load]);

  async function registerImportDocument(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!detail) return;

    const total = Number(documentTotal || 0);
    const exchangeRate = Number(documentExchangeRate || 1);
    const taxRate = Number(documentTaxRate || 0);
    const fob = Number(documentFob || 0);

    if (!documentNumber.trim()) {
      setError("Ingresa el número o correlativo del documento.");
      return;
    }

    if (!Number.isFinite(total) || total < 0) {
      setError("El total del documento no es válido.");
      return;
    }

    if (!Number.isFinite(exchangeRate) || exchangeRate <= 0) {
      setError("El tipo de cambio del documento no es válido.");
      return;
    }

    const items = documentIncludeItems
      ? detail.items.map((item) => ({
          catalog_item_id: item.catalog_item_id,
          sku: item.sku,
          description: item.description,
          quantity: Number(item.quantity),
          unit_price: Number(item.unit_cost),
          line_total: Number(item.quantity) * Number(item.unit_cost),
          fob_unit: Number(item.unit_cost),
          fob_total: Number(item.quantity) * Number(item.unit_cost),
        }))
      : [];

    setDocumentBusy(true);
    setError(null);
    setCostSuccess(null);

    const { error: documentError } = await supabase.rpc(
      "create_import_document",
      {
        p_import_shipment_id: detail.id,
        p_supplier_id: detail.supplier_id,
        p_document_type: documentType,
        p_document_scope: documentScope,
        p_series: documentSeries.trim() || null,
        p_document_number: documentNumber.trim(),
        p_issue_date: documentDate || null,
        p_currency_code: documentCurrency.toUpperCase(),
        p_exchange_rate: exchangeRate,
        p_is_national: documentNational,
        p_total_amount: total,
        p_tax_rate: taxRate,
        p_taxable_amount: null,
        p_tax_amount: null,
        p_fob_amount: fob,
        p_document_reference: documentReference.trim() || null,
        p_notes: documentNotes.trim() || null,
        p_items: items,
      },
    );

    setDocumentBusy(false);

    if (documentError) {
      setError(documentError.message);
      return;
    }

    setDocumentSeries("");
    setDocumentNumber("");
    setDocumentTotal("");
    setDocumentFob("");
    setDocumentReference("");
    setDocumentNotes("");
    setCostSuccess("Documento agregado al expediente de importación.");
    await load();
  }

  async function registerCustomsCalculation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!detail) return;

    const numberOrNull = (value: string) =>
      value.trim() === "" ? null : Number(value);

    setCustomsBusy(true);
    setError(null);
    setCostSuccess(null);

    const { error: customsError } = await supabase.rpc(
      "create_import_customs_calculation",
      {
        p_import_shipment_id: detail.id,
        p_declaration_type: duaType,
        p_declaration_number: duaNumber.trim(),
        p_customs_office: duaOffice.trim() || null,
        p_declaration_date: duaDate || null,
        p_exchange_rate: Number(duaTc || 1),
        p_fob_amount: Number(duaFob || 0),
        p_freight_amount: Number(duaFreight || 0),
        p_insurance_amount: Number(duaInsurance || 0),
        p_other_adjustments_amount: Number(duaAdjustments || 0),
        p_customs_value_amount: numberOrNull(duaCustomsValue),
        p_ad_valorem_rate: Number(duaAdValoremRate || 0),
        p_ad_valorem_amount_pen: numberOrNull(duaAdValoremManual),
        p_isc_rate: Number(duaIscRate || 0),
        p_isc_amount_pen: numberOrNull(duaIscManual),
        p_igv_rate: Number(duaIgvRate || 0),
        p_igv_amount_pen: numberOrNull(duaIgvManual),
        p_perception_rate: Number(duaPerceptionRate || 0),
        p_perception_amount_pen: numberOrNull(duaPerceptionManual),
        p_notes: null,
      },
    );

    setCustomsBusy(false);

    if (customsError) {
      setError(customsError.message);
      return;
    }

    setCostSuccess("Declaración aduanera y tributos registrados.");
    await load();
  }

  function exportImportDossier() {
    if (!detail) return;

    downloadCsv(
      `expediente-importacion-${detail.import_number}.csv`,
      [
        "tipo_documento",
        "serie",
        "numero",
        "fecha",
        "proveedor",
        "moneda",
        "tipo_cambio",
        "nacional",
        "base",
        "igv",
        "total",
        "fob",
        "items",
        "referencia",
      ],
      documents.map((row) => ({
        tipo_documento: IMPORT_DOCUMENT_LABELS[row.document_type] ?? row.document_type,
        serie: row.series ?? "",
        numero: row.document_number,
        fecha: row.issue_date,
        proveedor: row.supplier_name,
        moneda: row.currency_code,
        tipo_cambio: row.exchange_rate,
        nacional: row.is_national ? "SI" : "NO",
        base: row.taxable_amount,
        igv: row.tax_amount,
        total: row.total_amount,
        fob: row.fob_amount,
        items: row.item_count,
        referencia: row.document_reference ?? "",
      })),
    );
  }

  async function registerImportCost(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!detail) return;

    const amount = Number(costAmount);
    const exchangeRate = Number(costExchangeRate);

    if (!costDescription.trim()) {
      setError("Ingresa una descripción del costo.");
      return;
    }

    if (!Number.isFinite(amount) || amount < 0) {
      setError("El importe del costo no es válido.");
      return;
    }

    if (!Number.isFinite(exchangeRate) || exchangeRate <= 0) {
      setError("El tipo de cambio no es válido.");
      return;
    }

    setCostBusy(true);
    setError(null);
    setCostSuccess(null);

    const { error: costError } = await supabase.rpc("create_import_cost", {
      p_import_shipment_id: detail.id,
      p_supplier_id: null,
      p_cost_type: costType,
      p_description: costDescription.trim(),
      p_currency_code: costCurrency.toUpperCase(),
      p_amount: amount,
      p_exchange_rate: exchangeRate,
      p_capitalizable: costCapitalizable,
      p_recoverable_tax: costRecoverableTax,
      p_document_reference: null,
      p_expense_date: new Date().toISOString().slice(0, 10),
      p_notes: null,
      p_allocation_method: costAllocation,
      p_purchase_order_item_id: null,
    });

    if (costError) {
      setCostBusy(false);
      setError(costError.message);
      return;
    }

    const { error: calcError } = await supabase.rpc("calculate_import_landed_cost", {
      p_import_shipment_id: detail.id,
    });

    setCostBusy(false);

    if (calcError) {
      setError(calcError.message);
      return;
    }

    setCostDescription("");
    setCostAmount("");
    setCostSuccess("Costo registrado y distribuido por SKU.");
    await load();
  }

  async function applyCatalogPricing() {
    if (!detail) return;

    const markup = markupPercent.trim() === "" ? null : Number(markupPercent);
    if (markup !== null && (!Number.isFinite(markup) || markup < 0)) {
      setError("El margen no es válido.");
      return;
    }

    setPricingBusy(true);
    setError(null);
    setCostSuccess(null);

    const { data, error: pricingError } = await supabase.rpc(
      "apply_import_landed_cost_to_catalog",
      {
        p_import_shipment_id: detail.id,
        p_markup_percent: markup,
      },
    );

    setPricingBusy(false);

    if (pricingError) {
      setError(pricingError.message);
      return;
    }

    setCostSuccess(
      `Costos aplicados al catálogo: ${Number(data ?? 0)} SKU actualizados.`,
    );
    await load();
  }

  if (!selectedOrganization) {
    return (
      <div className="page">
        <section className="empty-state">
          <strong>Selecciona una empresa.</strong>
        </section>
      </div>
    );
  }

  if (loading && !detail) {
    return (
      <div className="page">
        <section className="empty-state">
          <strong>Cargando importación…</strong>
        </section>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="page">
        <div className="inline-alert">{error ?? "Importación no disponible."}</div>
        <Link className="compact-button import-detail-back" to="/importaciones">
          Volver a importaciones
        </Link>
      </div>
    );
  }

  const sourceCurrency = detail.supplier_currency_code ?? "USD";

  const controls = diagnostic
    ? [
        ["Compra recibida", diagnostic.purchase_received_ok],
        ["Embarque recibido", diagnostic.shipment_received_ok],
        ["Cantidad compra", diagnostic.purchase_quantity_ok],
        ["Cantidad embarque", diagnostic.shipment_quantity_ok],
        ["Cantidad recepción", diagnostic.receipt_quantity_ok],
        ["Cantidad inventario", diagnostic.inventory_quantity_ok],
        ["Costo landed", diagnostic.landed_cost_exists_ok],
        ["Declaración aduanera", diagnostic.customs_declaration_ok],
        ["Recepción vs landed", diagnostic.receipt_landed_reconciliation_ok],
        ["Inventario vs landed", diagnostic.inventory_landed_reconciliation_ok],
        ["Recepción vs inventario", diagnostic.receipt_inventory_reconciliation_ok],
      ]
    : [];

  return (
    <div className="page">
      <header className="page-header page-header-actions">
        <div>
          <span className="eyebrow">Importación</span>
          <h1>{detail.import_number}</h1>
          <p>
            <Link
              className="record-inline-link"
              to={`/proveedores/${detail.supplier_id}`}
            >
              {detail.supplier_name}
            </Link>
          </p>
        </div>

        <div className="quotation-actions">
          <button
            className="ghost-button"
            type="button"
            onClick={exportImportDossier}
            disabled={documents.length === 0}
          >
            Exportar expediente
          </button>
          <Link className="ghost-button import-detail-back" to="/importaciones">
            Volver
          </Link>
        </div>
      </header>

      {error ? <div className="inline-alert">{error}</div> : null}

      <section className="import-detail-summary">
        <div className="metric-card">
          <span>Estado</span>
          <strong className="import-detail-state">{IMPORT_STATUS_LABELS[detail.status.toLowerCase()] ?? detail.status}</strong>
        </div>
        <div className="metric-card">
          <span>Ítems</span>
          <strong>{detail.items.length}</strong>
        </div>
        <div className="metric-card">
          <span>Costo landed</span>
          <strong>{money(landedCost?.landed_cost_pen ?? 0)}</strong>
        </div>
        <div className="metric-card">
          <span>Cierre</span>
          <strong className="import-detail-state">
            {diagnostic?.ready_to_close ? "Lista" : "Pendiente"}
          </strong>
        </div>
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Análisis de importación</span>
            <h2>Indicadores de costo y carga tributaria</h2>
          </div>
        </div>

        <div className="import-control-grid">
          <div>
            <span>Documentos</span>
            <strong>{Number(analysis?.document_count ?? 0)}</strong>
          </div>
          <div>
            <span>Documentos nacionales</span>
            <strong>{Number(analysis?.domestic_document_count ?? 0)}</strong>
          </div>
          <div>
            <span>IGV nacional</span>
            <strong>{money(analysis?.domestic_igv_pen ?? 0)}</strong>
          </div>
          <div>
            <span>FOB aduanero</span>
            <strong>{sourceMoney(analysis?.customs_fob ?? 0, "USD")}</strong>
          </div>
          <div>
            <span>CIF / valor aduanero</span>
            <strong>{sourceMoney(analysis?.customs_value ?? 0, "USD")}</strong>
          </div>
          <div>
            <span>Tributos aduaneros</span>
            <strong>{money(analysis?.customs_total_payable_pen ?? 0)}</strong>
          </div>
          <div>
            <span>Factor CIF / FOB</span>
            <strong>{Number(analysis?.cif_fob_factor ?? 0).toFixed(4)}</strong>
          </div>
          <div>
            <span>Factor costo landed</span>
            <strong>{Number(analysis?.landed_factor ?? 0).toFixed(4)}</strong>
          </div>
          <div>
            <span>Carga tributaria</span>
            <strong>{Number(analysis?.tax_burden_percent ?? 0).toFixed(2)}%</strong>
          </div>
        </div>
      </section>

      <section className="import-detail-grid">
        <article className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Compra</span>
              <h2>{detail.purchase_number}</h2>
            </div>
            <Link
              className="compact-button import-detail-link"
              to={`/compras/${detail.purchase_order_id}`}
            >
              Abrir orden
            </Link>
          </div>

          <dl className="import-detail-meta">
            <div>
              <dt>Proveedor</dt>
              <dd>
                <Link
                  className="record-inline-link"
                  to={`/proveedores/${detail.supplier_id}`}
                >
                  {detail.supplier_name}
                </Link>
              </dd>
            </div>
            <div>
              <dt>Moneda</dt>
              <dd>{sourceCurrency}</dd>
            </div>
            <div>
              <dt>Tipo de cambio</dt>
              <dd>{detail.exchange_rate ?? "—"}</dd>
            </div>
            <div>
              <dt>Fuente TC</dt>
              <dd>{detail.exchange_rate_source ?? "—"}</dd>
            </div>
          </dl>
        </article>

        <article className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Logística</span>
              <h2>Ruta y transporte</h2>
            </div>
          </div>

          <dl className="import-detail-meta">
            <div>
              <dt>Origen</dt>
              <dd>{detail.origin_country_code ?? "—"}</dd>
            </div>
            <div>
              <dt>Destino</dt>
              <dd>{detail.destination_country_code ?? "—"}</dd>
            </div>
            <div>
              <dt>Incoterm</dt>
              <dd>{detail.incoterm ?? "—"}</dd>
            </div>
            <div>
              <dt>Referencia</dt>
              <dd>{detail.transport_reference ?? "—"}</dd>
            </div>
            <div>
              <dt>Embarque</dt>
              <dd>{detail.shipment_date ?? "—"}</dd>
            </div>
            <div>
              <dt>Llegada estimada</dt>
              <dd>{detail.estimated_arrival_date ?? "—"}</dd>
            </div>
            <div>
              <dt>Llegada real</dt>
              <dd>{detail.arrival_date ?? "—"}</dd>
            </div>
            <div>
              <dt>Distribución</dt>
              <dd>{detail.allocation_method ?? "—"}</dd>
            </div>
          </dl>
        </article>
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Expediente documental</span>
            <h2>Facturas, cotizaciones, notas y documentos vinculados</h2>
          </div>
        </div>

        <form className="import-cost-form" onSubmit={registerImportDocument}>
          <label>
            Tipo
            <select value={documentType} onChange={(event) => setDocumentType(event.target.value)}>
              {Object.entries(IMPORT_DOCUMENT_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>

          <label>
            Origen
            <select
              value={documentScope}
              onChange={(event) => {
                const value = event.target.value;
                setDocumentScope(value);
                setDocumentNational(value === "domestic");
                if (value === "domestic") setDocumentCurrency("PEN");
              }}
            >
              <option value="foreign">Exterior</option>
              <option value="domestic">Nacional</option>
              <option value="customs">Aduanas</option>
              <option value="logistics">Logística</option>
              <option value="other">Otro</option>
            </select>
          </label>

          <label>
            Fecha
            <input type="date" value={documentDate} onChange={(event) => setDocumentDate(event.target.value)} />
          </label>

          <label>
            Serie
            <input value={documentSeries} onChange={(event) => setDocumentSeries(event.target.value.toUpperCase())} />
          </label>

          <label>
            Correlativo / número
            <input required value={documentNumber} onChange={(event) => setDocumentNumber(event.target.value.toUpperCase())} />
          </label>

          <label>
            Moneda
            <input maxLength={3} value={documentCurrency} onChange={(event) => setDocumentCurrency(event.target.value.toUpperCase())} />
          </label>

          <label>
            Tipo de cambio
            <input type="number" min="0.000001" step="0.000001" value={documentExchangeRate} onChange={(event) => setDocumentExchangeRate(event.target.value)} />
          </label>

          <label>
            Total documento
            <input type="number" min="0" step="0.01" value={documentTotal} onChange={(event) => setDocumentTotal(event.target.value)} />
          </label>

          <label>
            Tasa IGV %
            <input
              type="number"
              min="0"
              step="0.01"
              value={documentTaxRate}
              disabled={!documentNational}
              onChange={(event) => setDocumentTaxRate(event.target.value)}
            />
          </label>

          <label>
            Monto FOB
            <input type="number" min="0" step="0.01" value={documentFob} onChange={(event) => setDocumentFob(event.target.value)} />
          </label>

          <label className="import-cost-wide">
            Referencia
            <input value={documentReference} onChange={(event) => setDocumentReference(event.target.value)} placeholder="OC, BL, AWB, póliza, etc." />
          </label>

          <label className="import-cost-check">
            <input
              type="checkbox"
              checked={documentIncludeItems}
              onChange={(event) => setDocumentIncludeItems(event.target.checked)}
            />
            Asociar todos los ítems del embarque
          </label>

          <label className="import-cost-wide">
            Notas
            <input value={documentNotes} onChange={(event) => setDocumentNotes(event.target.value)} />
          </label>

          <div className="form-actions import-cost-wide">
            <button className="primary-button" type="submit" disabled={documentBusy}>
              {documentBusy ? "Guardando…" : "Agregar documento"}
            </button>
          </div>
        </form>

        {documents.length > 0 ? (
          <div className="import-pricing-grid import-documents-grid">
            {documents.map((row) => (
              <article className="import-pricing-card" key={row.id}>
                <strong>{IMPORT_DOCUMENT_LABELS[row.document_type] ?? row.document_type}</strong>
                <span>{[row.series, row.document_number].filter(Boolean).join("-")} · {row.issue_date}</span>
                <dl>
                  <div>
                    <dt>Proveedor</dt>
                    <dd>{row.supplier_name}</dd>
                  </div>
                  <div>
                    <dt>Total</dt>
                    <dd>{sourceMoney(row.total_amount, row.currency_code)}</dd>
                  </div>
                  <div>
                    <dt>Base</dt>
                    <dd>{sourceMoney(row.taxable_amount, row.currency_code)}</dd>
                  </div>
                  <div>
                    <dt>IGV</dt>
                    <dd>{sourceMoney(row.tax_amount, row.currency_code)}</dd>
                  </div>
                  <div>
                    <dt>FOB</dt>
                    <dd>{sourceMoney(row.fob_amount, row.currency_code)}</dd>
                  </div>
                  <div>
                    <dt>Ítems</dt>
                    <dd>{Number(row.item_count)}</dd>
                  </div>
                </dl>
              </article>
            ))}
          </div>
        ) : (
          <p className="settings-empty">Aún no hay documentos vinculados.</p>
        )}
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">DUA / DAM</span>
            <h2>Valor aduanero y tributos de importación</h2>
          </div>
        </div>

        <p className="settings-empty">
          El valor CIF se calcula como FOB + flete + seguro + ajustes. Los importes de tributos pueden calcularse por tasa o ingresarse manualmente.
        </p>

        <form className="import-cost-form" onSubmit={registerCustomsCalculation}>
          <label>
            Tipo declaración
            <select value={duaType} onChange={(event) => setDuaType(event.target.value)}>
              <option value="DAM">DAM</option>
              <option value="DSI">DSI</option>
              <option value="COURIER">Courier</option>
              <option value="OTHER">Otra</option>
            </select>
          </label>

          <label className="import-cost-wide">
            Número declaración
            <input required value={duaNumber} onChange={(event) => setDuaNumber(event.target.value.toUpperCase())} />
          </label>

          <label>
            Fecha
            <input type="date" value={duaDate} onChange={(event) => setDuaDate(event.target.value)} />
          </label>

          <label>
            Aduana
            <input value={duaOffice} onChange={(event) => setDuaOffice(event.target.value)} />
          </label>

          <label>
            TC aduanero
            <input type="number" min="0.000001" step="0.000001" value={duaTc} onChange={(event) => setDuaTc(event.target.value)} />
          </label>

          <label>
            FOB USD
            <input type="number" min="0" step="0.01" value={duaFob} onChange={(event) => setDuaFob(event.target.value)} />
          </label>

          <label>
            Flete USD
            <input type="number" min="0" step="0.01" value={duaFreight} onChange={(event) => setDuaFreight(event.target.value)} />
          </label>

          <label>
            Seguro USD
            <input type="number" min="0" step="0.01" value={duaInsurance} onChange={(event) => setDuaInsurance(event.target.value)} />
          </label>

          <label>
            Ajustes USD
            <input type="number" step="0.01" value={duaAdjustments} onChange={(event) => setDuaAdjustments(event.target.value)} />
          </label>

          <label>
            CIF manual USD
            <input type="number" min="0" step="0.01" value={duaCustomsValue} onChange={(event) => setDuaCustomsValue(event.target.value)} placeholder="Vacío = cálculo automático" />
          </label>

          <label>
            Ad Valorem %
            <input type="number" min="0" step="0.01" value={duaAdValoremRate} onChange={(event) => setDuaAdValoremRate(event.target.value)} />
          </label>

          <label>
            Ad Valorem manual PEN
            <input type="number" min="0" step="0.01" value={duaAdValoremManual} onChange={(event) => setDuaAdValoremManual(event.target.value)} />
          </label>

          <label>
            ISC %
            <input type="number" min="0" step="0.01" value={duaIscRate} onChange={(event) => setDuaIscRate(event.target.value)} />
          </label>

          <label>
            ISC manual PEN
            <input type="number" min="0" step="0.01" value={duaIscManual} onChange={(event) => setDuaIscManual(event.target.value)} />
          </label>

          <label>
            IGV %
            <input type="number" min="0" step="0.01" value={duaIgvRate} onChange={(event) => setDuaIgvRate(event.target.value)} />
          </label>

          <label>
            IGV manual PEN
            <input type="number" min="0" step="0.01" value={duaIgvManual} onChange={(event) => setDuaIgvManual(event.target.value)} />
          </label>

          <label>
            Percepción %
            <input type="number" min="0" step="0.01" value={duaPerceptionRate} onChange={(event) => setDuaPerceptionRate(event.target.value)} />
          </label>

          <label>
            Percepción manual PEN
            <input type="number" min="0" step="0.01" value={duaPerceptionManual} onChange={(event) => setDuaPerceptionManual(event.target.value)} />
          </label>

          <div className="form-actions import-cost-wide">
            <button className="primary-button" type="submit" disabled={customsBusy}>
              {customsBusy ? "Calculando…" : "Registrar DUA y calcular tributos"}
            </button>
          </div>
        </form>
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Mercadería</span>
            <h2>Ítems embarcados</h2>
          </div>
        </div>

        <div className="import-detail-items">
          {detail.items.map((item) => (
            <article className="import-detail-item" key={item.id}>
              <div>
                <strong>{item.description}</strong>
                <span>{item.sku ?? "Ítem sin SKU"}</span>
              </div>

              <dl>
                <div>
                  <dt>Cantidad</dt>
                  <dd>{Number(item.quantity)}</dd>
                </div>
                <div>
                  <dt>Costo unitario</dt>
                  <dd>{sourceMoney(item.unit_cost, item.currency_code)}</dd>
                </div>
                <div>
                  <dt>Peso unit.</dt>
                  <dd>
                    {item.unit_weight_kg == null
                      ? "—"
                      : `${Number(item.unit_weight_kg)} kg`}
                  </dd>
                </div>
                <div>
                  <dt>Volumen unit.</dt>
                  <dd>
                    {item.unit_volume_m3 == null
                      ? "—"
                      : `${Number(item.unit_volume_m3)} m³`}
                  </dd>
                </div>
              </dl>
            </article>
          ))}
        </div>
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Costos de importación</span>
            <h2>Registrar gasto y distribuir por SKU</h2>
          </div>
        </div>

        <form className="import-cost-form" onSubmit={registerImportCost}>
          <label>
            Tipo de costo
            <select value={costType} onChange={(event) => setCostType(event.target.value)}>
              <option value="freight">Flete</option>
              <option value="insurance">Seguro</option>
              <option value="customs_duty">Derecho aduanero</option>
              <option value="customs_agent">Agente de aduanas</option>
              <option value="international_transport">Transporte internacional</option>
              <option value="local_transport">Transporte local</option>
              <option value="port_charge">Gasto portuario</option>
              <option value="terminal_charge">Terminal</option>
              <option value="storage">Almacenaje</option>
              <option value="inspection">Inspección</option>
              <option value="handling">Manipuleo</option>
              <option value="bank_charge">Gasto bancario</option>
              <option value="tax">Impuesto</option>
              <option value="perception">Percepción</option>
              <option value="other">Otro</option>
            </select>
          </label>

          <label className="import-cost-wide">
            Descripción
            <input
              value={costDescription}
              onChange={(event) => setCostDescription(event.target.value)}
              placeholder="Ej. Flete marítimo"
            />
          </label>

          <label>
            Moneda
            <input
              value={costCurrency}
              maxLength={3}
              onChange={(event) => setCostCurrency(event.target.value.toUpperCase())}
            />
          </label>

          <label>
            Importe
            <input
              type="number"
              min="0"
              step="0.01"
              value={costAmount}
              onChange={(event) => setCostAmount(event.target.value)}
            />
          </label>

          <label>
            Tipo de cambio
            <input
              type="number"
              min="0.000001"
              step="0.000001"
              value={costExchangeRate}
              onChange={(event) => setCostExchangeRate(event.target.value)}
            />
          </label>

          <label>
            Distribuir por
            <select
              value={costAllocation}
              onChange={(event) => setCostAllocation(event.target.value)}
            >
              <option value="value">Valor</option>
              <option value="quantity">Cantidad</option>
              <option value="weight">Peso</option>
              <option value="volume">Volumen</option>
              <option value="direct">Directa</option>
            </select>
          </label>

          <label className="import-cost-check">
            <input
              type="checkbox"
              checked={costCapitalizable}
              onChange={(event) => {
                setCostCapitalizable(event.target.checked);
                if (event.target.checked) setCostRecoverableTax(false);
              }}
            />
            Capitalizable
          </label>

          <label className="import-cost-check">
            <input
              type="checkbox"
              checked={costRecoverableTax}
              onChange={(event) => {
                setCostRecoverableTax(event.target.checked);
                if (event.target.checked) setCostCapitalizable(false);
              }}
            />
            Impuesto recuperable
          </label>

          <div className="form-actions import-cost-wide">
            <button className="primary-button" type="submit" disabled={costBusy}>
              {costBusy ? "Calculando…" : "Registrar y recalcular"}
            </button>
          </div>
        </form>

        {costSuccess ? <div className="form-success import-cost-message">{costSuccess}</div> : null}
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Costo landed</span>
            <h2>Resumen en PEN</h2>
          </div>
        </div>

        <div className="import-cost-grid">
          <div>
            <span>Mercadería</span>
            <strong>{money(landedCost?.merchandise_cost_pen ?? 0)}</strong>
          </div>
          <div>
            <span>Costos capitalizables</span>
            <strong>{money(landedCost?.capitalizable_cost_pen ?? 0)}</strong>
          </div>
          <div>
            <span>Impuestos recuperables</span>
            <strong>{money(landedCost?.recoverable_tax_pen ?? 0)}</strong>
          </div>
          <div>
            <span>No capitalizable</span>
            <strong>{money(landedCost?.noncapitalizable_cost_pen ?? 0)}</strong>
          </div>
          <div className="import-cost-total">
            <span>Costo landed total</span>
            <strong>{money(landedCost?.landed_cost_pen ?? 0)}</strong>
          </div>
        </div>
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Precios por SKU</span>
            <h2>Costo landed y catálogo</h2>
          </div>
        </div>

        {itemCosts.length === 0 ? (
          <p className="settings-empty">
            Calcula primero el costo landed para obtener valores por SKU.
          </p>
        ) : (
          <>
            <div className="import-pricing-grid">
              {itemCosts.map((item) => (
                <article className="import-pricing-card" key={item.catalog_item_id}>
                  <strong>{item.item_name}</strong>
                  <span>{item.sku ?? "Sin SKU"}</span>
                  <dl>
                    <div>
                      <dt>Costo landed unit.</dt>
                      <dd>{money(item.landed_unit_cost_pen)}</dd>
                    </div>
                    <div>
                      <dt>Costo catálogo</dt>
                      <dd>{money(item.current_cost_price)}</dd>
                    </div>
                    <div>
                      <dt>Precio venta actual</dt>
                      <dd>{money(item.current_sale_price)}</dd>
                    </div>
                  </dl>
                </article>
              ))}
            </div>

            <div className="import-pricing-action">
              <label>
                Margen para precio de venta %
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={markupPercent}
                  onChange={(event) => setMarkupPercent(event.target.value)}
                />
              </label>
              <button
                className="primary-button"
                type="button"
                disabled={pricingBusy}
                onClick={() => void applyCatalogPricing()}
              >
                {pricingBusy ? "Aplicando…" : "Aplicar costos y precios al catálogo"}
              </button>
            </div>
          </>
        )}
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Aduanas</span>
            <h2>Declaración y levante</h2>
          </div>
        </div>

        <dl className="import-detail-meta">
          <div>
            <dt>Declaración</dt>
            <dd>{detail.customs_declaration_number ?? "—"}</dd>
          </div>
          <div>
            <dt>Aduana</dt>
            <dd>{detail.customs_office ?? "—"}</dd>
          </div>
          <div>
            <dt>Levante</dt>
            <dd>{detail.customs_clearance_date ?? "—"}</dd>
          </div>
          <div>
            <dt>Declaraciones liquidadas</dt>
            <dd>
              {diagnostic
                ? `${Number(diagnostic.liquidated_declaration_count)} / ${Number(
                    diagnostic.declaration_count,
                  )}`
                : "—"}
            </dd>
          </div>
        </dl>
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Cierre</span>
            <h2>Diagnóstico de conciliación</h2>
          </div>
          {diagnostic ? (
            <span
              className={
                diagnostic.ready_to_close
                  ? "status-badge import-ready"
                  : "status-badge"
              }
            >
              {diagnostic.diagnostic_status}
            </span>
          ) : null}
        </div>

        {diagnostic?.blocking_reason ? (
          <div className="inline-alert">{diagnostic.blocking_reason}</div>
        ) : null}

        <div className="import-control-grid">
          {controls.map(([label, value]) => (
            <div key={String(label)}>
              <span>{String(label)}</span>
              <strong className={value ? "import-control-ok" : ""}>
                {checkLabel(Boolean(value))}
              </strong>
            </div>
          ))}
        </div>

        {diagnostic ? (
          <div className="import-reconciliation-grid">
            <div>
              <span>Cantidad compra</span>
              <strong>{Number(diagnostic.ordered_quantity)}</strong>
            </div>
            <div>
              <span>Cantidad recibida</span>
              <strong>{Number(diagnostic.receipt_quantity)}</strong>
            </div>
            <div>
              <span>Valor recepción</span>
              <strong>{money(diagnostic.receipt_value_pen)}</strong>
            </div>
            <div>
              <span>Valor inventario</span>
              <strong>{money(diagnostic.inventory_value_pen)}</strong>
            </div>
          </div>
        ) : null}
      </section>

      {detail.notes ? (
        <section className="panel-card">
          <span className="eyebrow">Notas</span>
          <p className="import-detail-notes">{detail.notes}</p>
        </section>
      ) : null}
    </div>
  );
}
